/**
 * Server-held user settings — the single source of truth for language, theme,
 * grid size and the first-run "explore" choice.
 *
 * Nothing here is persisted client-side. That has one visible consequence
 * worth knowing: settings can only be applied once the session has loaded, so
 * the very first paint (and any pre-authentication screen, e.g. the landing
 * page) uses the browser's own defaults — detected language, system colour
 * scheme — and switches to the stored values a moment later. Caching them in
 * localStorage would remove that flash, at the cost of keeping settings on the
 * client, which is exactly what this deliberately does not do.
 *
 * Reads come from `getSession`, so they belong to the user being ACTED AS:
 * impersonating someone shows their language and theme, not yours.
 */
import { useCallback } from "react";
import type { UpdatePreferencesInput, UserPreferencesDto } from "@mmm/contracts";
import { updatePreferences } from "../api/commands.js";
import { getSession } from "../api/queries.js";
import { useQuery } from "../api/useQuery.js";

const NOT_LOADED: UserPreferencesDto = {
  language: null,
  theme: null,
  gridSize: null,
  exploredWithoutSubject: false,
};

export interface PreferencesState {
  preferences: UserPreferencesDto;
  /** False until the session has answered — callers should not apply defaults over stored values before this. */
  loaded: boolean;
  save: (patch: UpdatePreferencesInput) => Promise<void>;
}

export function usePreferences(): PreferencesState {
  const sessionQuery = useQuery(getSession, undefined);
  const preferences = sessionQuery.data?.preferences;

  const save = useCallback(async (patch: UpdatePreferencesInput) => {
    // The command invalidates every query on success (commands.ts run()), so
    // getSession refetches and the new values flow back through this hook —
    // no separate local copy to keep in sync.
    await updatePreferences(patch);
  }, []);

  return {
    preferences: preferences ?? NOT_LOADED,
    loaded: preferences !== undefined,
    save,
  };
}
